/**
 * Galexia Arena — THE contract file (docs/ARENA_PLAN.md, P0).
 *
 * Sibling agents (the review UI and the judge/leaderboard) import ONLY from
 * this module and the API routes under app/api/arena. Everything shared —
 * enums, zod payload schemas, adapter/engine interfaces, export/blind view
 * shapes, and config-key constants — is exported from here.
 */
import { z } from 'zod';
import type {
  ArenaExperiment,
  ArenaVariant,
  ArenaGeneration,
  ArenaReview,
  ArenaJudgment,
  ArenaTaskSpec,
  ArenaChecklistItem,
} from '@/db/schema';

// Re-export the DB row + jsonb types so consumers have a single import surface.
export type {
  ArenaExperiment,
  NewArenaExperiment,
  ArenaVariant,
  NewArenaVariant,
  ArenaGeneration,
  NewArenaGeneration,
  ArenaReview,
  NewArenaReview,
  ArenaJudgment,
  NewArenaJudgment,
  ArenaTaskSpec,
  ArenaChecklistItem,
} from '@/db/schema';

// ---------------------------------------------------------------------------
// Enums (const arrays + derived unions). Single source of truth for the DB
// text() columns and the zod schemas below.
// ---------------------------------------------------------------------------
export const ARENA_MODALITIES = ['image', 'video', 'avatar', 'html-viz'] as const;
export type ArenaModality = (typeof ARENA_MODALITIES)[number];

export const ARENA_SEED_POLICIES = ['fixed', 'random'] as const;
export type ArenaSeedPolicy = (typeof ARENA_SEED_POLICIES)[number];

export const ARENA_GENERATOR_KINDS = ['fal', 'modal-avatar', 'llm-html'] as const;
export type ArenaGeneratorKind = (typeof ARENA_GENERATOR_KINDS)[number];

export const ARENA_EXPERIMENT_STATUSES = [
  'draft', 'running', 'completed', 'failed', 'budget_exhausted', 'canceled',
] as const;
export type ArenaExperimentStatus = (typeof ARENA_EXPERIMENT_STATUSES)[number];

export const ARENA_GENERATION_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export type ArenaGenerationStatus = (typeof ARENA_GENERATION_STATUSES)[number];

export const ARENA_ARTIFACT_TYPES = ['image', 'video', 'html', 'screenshot'] as const;
export type ArenaArtifactType = (typeof ARENA_ARTIFACT_TYPES)[number];

export const ARENA_REVIEW_MODES = ['pairwise', 'pick', 'rubric'] as const;
export type ArenaReviewMode = (typeof ARENA_REVIEW_MODES)[number];

export const ARENA_JUDGMENT_VERDICTS = ['accept', 'reject', 'borderline'] as const;
export type ArenaJudgmentVerdict = (typeof ARENA_JUDGMENT_VERDICTS)[number];

export const ARENA_EXPORT_FORMATS = ['json', 'csv'] as const;
export type ArenaExportFormat = (typeof ARENA_EXPORT_FORMATS)[number];

/** Which generation statuses are terminal (never re-run within a run). */
export const ARENA_TERMINAL_GENERATION_STATUSES: ReadonlySet<ArenaGenerationStatus> = new Set([
  'succeeded', 'failed',
]);

// ---------------------------------------------------------------------------
// Config keys (mirror lib/config.ts DEFAULTS). A4: all hot-swappable.
// ---------------------------------------------------------------------------
export const ARENA_CONFIG_KEYS = {
  defaultBudgetCap: 'arena.experiment.defaultBudgetCap',
  runConcurrency: 'arena.run.concurrency',
  pricing: 'arena.pricing',
  htmlvizModel: 'arena.htmlviz.model',
} as const;

export const ARENA_DEFAULT_BUDGET_CAP_CREDITS = 500;
export const ARENA_DEFAULT_RUN_CONCURRENCY = 4;

// ---------------------------------------------------------------------------
// API payload schemas (zod). Routes validate against these; siblings reuse them.
// ---------------------------------------------------------------------------
export const arenaTaskSpecSchema = z
  .object({
    prompt: z.string().optional(),
    brief: z.string().optional(),
    inputs: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

export const arenaVariantSpecSchema = z.object({
  /** Optional explicit label; auto-assigned A/B/C… when omitted. */
  label: z.string().trim().min(1).optional(),
  generatorKind: z.enum(ARENA_GENERATOR_KINDS),
  /** Model id / provider slug (fal endpoint id, avatar tier, or LLM id). */
  model: z.string().trim().optional(),
  provider: z.string().trim().optional(),
  /** Overrides taskSpec.prompt for this variant when present. */
  promptTemplate: z.string().optional(),
  configOverrides: z.record(z.string(), z.unknown()).optional(),
  skillVersion: z.string().optional(),
});
export type ArenaVariantSpec = z.infer<typeof arenaVariantSpecSchema>;

export const createArenaExperimentSchema = z
  .object({
    name: z.string().trim().min(1),
    description: z.string().trim().optional(),
    modality: z.enum(ARENA_MODALITIES),
    taskSpec: arenaTaskSpecSchema,
    variants: z.array(arenaVariantSpecSchema).min(1),
    seedPolicy: z.enum(ARENA_SEED_POLICIES).default('random'),
    fixedSeed: z.number().int().optional(),
    sampleCountPerVariant: z.number().int().min(1).max(50).default(1),
    /** Omit to use config arena.experiment.defaultBudgetCap. */
    budgetCapCredits: z.number().int().min(1).optional(),
  })
  .refine((v) => v.seedPolicy !== 'fixed' || typeof v.fixedSeed === 'number', {
    message: 'fixedSeed is required when seedPolicy is "fixed"',
    path: ['fixedSeed'],
  });
export type CreateArenaExperimentInput = z.infer<typeof createArenaExperimentSchema>;

export const listArenaGenerationsQuerySchema = z.object({
  status: z.enum(ARENA_GENERATION_STATUSES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  /** Strips model/provider/variant-label from the payload (blind review). */
  blindMode: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .optional()
    .transform((v) => v === true || v === 'true'),
});
export type ListArenaGenerationsQuery = z.infer<typeof listArenaGenerationsQuerySchema>;

export const arenaExportFormatSchema = z.enum(ARENA_EXPORT_FORMATS).default('json');

// ---------------------------------------------------------------------------
// Planning types (pure, unit-tested). expandVariants + expandGenerations.
// ---------------------------------------------------------------------------
export interface PlannedVariant {
  label: string;
  generatorKind: ArenaGeneratorKind;
  model: string | null;
  provider: string | null;
  promptTemplate: string | null;
  configOverrides: Record<string, unknown> | null;
  skillVersion: string | null;
  ordering: number;
}

export interface PlannedGeneration {
  /** Index into the ordered variant list (stable A=0, B=1, …). */
  variantIndex: number;
  variantLabel: string;
  sampleIndex: number;
  seed: number | null;
}

// ---------------------------------------------------------------------------
// Adapter contract. Each generatorKind maps to one ArenaAdapter.
// A thrown generate() => the generation fails in isolation (siblings continue).
// A returned result with artifactUrl=null but extraArtifacts/validationResults
// => a partial success (e.g. html produced, screenshot render failed).
// ---------------------------------------------------------------------------
export interface ArenaAdapterResult {
  artifactUrl: string | null;
  artifactType: ArenaArtifactType | null;
  extraArtifacts?: Record<string, unknown> | null;
  /** Actual provider cost in USD when known; null => unknown (never guessed). */
  costUsd?: number | null;
  providerJobId?: string | null;
  /** The EXACT prompt and request payload actually sent (provenance). */
  resolvedPrompt: string;
  resolvedConfig: Record<string, unknown>;
  validationResults?: Record<string, unknown> | null;
}

export interface ArenaRunContext {
  userId: string;
  experimentId: string;
  /**
   * Lazy get-or-create ONE Daytona sandbox for the whole run (html-viz render).
   * Returns null when Daytona is not configured. Reused across generations and
   * torn down by the engine at run end.
   */
  getHtmlRenderSandbox(): Promise<{ userId: string; sandboxId: string } | null>;
}

export interface ArenaAdapterContext {
  experiment: Pick<ArenaExperiment, 'id' | 'modality' | 'taskSpec'>;
  variant: ArenaVariant;
  sampleIndex: number;
  seed: number | null;
  /** The resolved prompt (variant.promptTemplate ?? taskSpec.prompt/brief). */
  prompt: string;
  runContext: ArenaRunContext;
}

export interface ArenaAdapter {
  readonly kind: ArenaGeneratorKind;
  /** Estimated USD cost of one generation, for the budget pre-check. */
  estimateCostUsd(ctx: ArenaAdapterContext): Promise<number | null> | number | null;
  generate(ctx: ArenaAdapterContext): Promise<ArenaAdapterResult>;
}

export type ArenaAdapterRegistry = Record<ArenaGeneratorKind, ArenaAdapter>;

// ---------------------------------------------------------------------------
// Store contract — the DB seam the engine writes through. The real impl lives
// in lib/arena/store.ts; tests inject an in-memory fake.
// ---------------------------------------------------------------------------
export interface ArenaGenerationPatch {
  status?: ArenaGenerationStatus;
  resolvedPrompt?: string | null;
  resolvedConfig?: Record<string, unknown> | null;
  artifactUrl?: string | null;
  artifactType?: ArenaArtifactType | null;
  extraArtifacts?: Record<string, unknown> | null;
  costUsd?: number | null;
  costCredits?: number;
  latencyMs?: number | null;
  providerJobId?: string | null;
  error?: string | null;
  validationResults?: Record<string, unknown> | null;
  completedAt?: Date | null;
}

export interface ArenaExperimentPatch {
  status?: ArenaExperimentStatus;
  costSpent?: number;
  startedAt?: Date | null;
  completedAt?: Date | null;
}

export interface ArenaStore {
  loadExperiment(experimentId: string): Promise<ArenaExperiment | null>;
  loadVariants(experimentId: string): Promise<ArenaVariant[]>;
  /** Existing generation rows for a run (idempotent re-run guard). */
  loadGenerations(experimentId: string): Promise<ArenaGeneration[]>;
  createGenerations(rows: NewArenaGenerationRow[]): Promise<ArenaGeneration[]>;
  updateGeneration(generationId: string, patch: ArenaGenerationPatch): Promise<void>;
  updateExperiment(experimentId: string, patch: ArenaExperimentPatch): Promise<void>;
}

/** Insert shape for a queued generation (subset of NewArenaGeneration). */
export interface NewArenaGenerationRow {
  experimentId: string;
  variantId: string;
  sampleIndex: number;
  status: ArenaGenerationStatus;
  seed: number | null;
}

// ---------------------------------------------------------------------------
// Run dependencies (injected — enables genuine engine unit tests).
// ---------------------------------------------------------------------------
export interface RunExperimentDeps {
  store: ArenaStore;
  adapters: ArenaAdapterRegistry;
  concurrency: number;
  runContext: ArenaRunContext;
  /** Injectable clock for latency; defaults to Date.now. */
  now?: () => number;
}

export interface RunExperimentResult {
  experimentId: string;
  status: ArenaExperimentStatus;
  costSpent: number;
  counts: Record<ArenaGenerationStatus, number>;
}

// ---------------------------------------------------------------------------
// Blind view (leak test asserts NO model/slug/provider/variant-label strings).
// ---------------------------------------------------------------------------
export interface BlindGenerationView {
  id: string;
  experimentId: string;
  /** Opaque variant key ("A"/"B"/…) derived from variant ordering. */
  variantKey: string;
  sampleIndex: number;
  status: ArenaGenerationStatus;
  artifactUrl: string | null;
  artifactType: ArenaArtifactType | null;
  /** Only non-identifying artifacts (screenshotUrl/posterUrl). */
  extraArtifacts: Record<string, unknown> | null;
  costUsd: number | null;
  costCredits: number;
  latencyMs: number | null;
  createdAt: string;
}

/** Full (non-blind) generation view for the review UI. */
export interface ArenaGenerationView extends BlindGenerationView {
  variantId: string;
  variantLabel: string;
  generatorKind: string | null;
  model: string | null;
  provider: string | null;
  seed: number | null;
  resolvedPrompt: string | null;
  resolvedConfig: Record<string, unknown> | null;
  error: string | null;
  validationResults: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Export (E5) — full-fidelity document.
// ---------------------------------------------------------------------------
export interface ArenaExportDocument {
  formatVersion: 1;
  exportedAt: string;
  experiment: ArenaExperiment;
  variants: ArenaVariant[];
  generations: ArenaGeneration[];
  reviews: ArenaReview[];
  judgments: ArenaJudgment[];
}
